import { useParams, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAmmoByDescriptor, useDataStore } from "@/lib/store/data-store";
import { Badge } from "@/components/ui/badge";
import { TraitBadgeList } from "@/components/ui/trait-badge-list";
import { WeaponImage } from "@/components/WeaponImage/WeaponImage";
import { formatDistance } from "@/lib/utils/format-distance";
import { formatTime } from "@/lib/utils/format-time";
import { getUnitsUsingAmmo } from "@/lib/utils/units-using-ammo";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { FieldDisplay } from "@/components/ui/FieldDisplay";
import { RelatedTools } from "@/components/RelatedTools";
import type { Division } from "@/lib/types/deck-builder";

function renderOptionalValue(value: string | number | undefined | null) {
  if (value === undefined || value === null || value === "") {
    return <span className="text-muted-foreground">-</span>;
  }
  return String(value);
}

function renderBoolean(value: boolean | undefined) {
  if (value === undefined) return <span className="text-muted-foreground">-</span>;
  return value ? "Yes" : "No";
}

export function WeaponDetail() {
  const { descriptorName } = useParams<{ descriptorName: string }>();
  const navigate = useNavigate();
  const { loadingWeapons, units, weapons, divisions } = useDataStore();

  // Decode the descriptor name from URL
  const decodedDescriptorName = descriptorName ? decodeURIComponent(descriptorName) : "";
  const ammo = useAmmoByDescriptor(decodedDescriptorName);

  // Find units using this ammo
  const unitsUsingAmmo = useMemo(() => {
    if (!ammo) return [];
    return getUnitsUsingAmmo(ammo.descriptorName, units, weapons);
  }, [ammo, units, weapons]);

  const divisionsMap = useMemo(() => {
    const m = new Map<string, Division>();
    (divisions || []).forEach((d) => m.set(d.descriptor, d));
    return m;
  }, [divisions]);

  const primaryUnit = unitsUsingAmmo[0];
  const deckBuilderTargetsFromWeapon = useMemo(() => {
    if (!primaryUnit?.divisions?.length) return undefined;
    return primaryUnit.divisions.map((d) => ({
      descriptor: d,
      name: divisionsMap.get(d)?.name ?? d,
    }));
  }, [primaryUnit, divisionsMap]);

  if (loadingWeapons) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="container mx-auto px-2 py-2">
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  if (!ammo) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="container mx-auto px-2 py-2">
          <Card>
            <CardContent className="py-4">
              <div className="text-center">
                <p className="text-destructive mb-2 text-sm">Ammunition not found</p>
                <Button onClick={() => navigate("/weapons")} variant="outline" size="sm">
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  Weapons List
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto px-4 py-1">
        <div>
          <Button
            onClick={() => navigate("/weapons")}
            variant="ghost"
            className="mb-1"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Weapons List
          </Button>
        </div>

        {primaryUnit ? (
          <div className="mb-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Related tools</p>
            <RelatedTools
              relatedUnitDescriptor={primaryUnit.descriptorName}
              deckBuilderTargets={deckBuilderTargetsFromWeapon}
            />
          </div>
        ) : null}

        {/* Header section */}
        <Card className="mb-4">
          <CardContent className="p-2 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-[250px_1fr] gap-4">
              {/* Weapon texture */}
              <div className="flex flex-col gap-4 min-w-0">
                <div className="w-full flex items-center justify-center">
                  <WeaponImage
                    textureId={ammo.textureId}
                    alt={ammo.name}
                    size="large"
                  />
                </div>

                {/* Traits */}
                {ammo.traits && ammo.traits.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-muted-foreground mb-2">Traits</div>
                    <TraitBadgeList specialities={ammo.traits} />
                  </div>
                )}
              </div>

              {/* Weapon info */}
              <div className="flex flex-col justify-between min-w-0">
                <div>
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <CardTitle className="text-2xl font-bold">{ammo.name}</CardTitle>
                  </div>
                  <p className="text-sm text-muted-foreground">{ammo.descriptorName}</p>
                  {ammo.minMaxCategory && (
                    <Badge variant="outline" className="mt-2 w-fit">
                      {ammo.minMaxCategory}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Damage Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Damage Statistics</CardTitle>
            </CardHeader>
            <CardContent className="p-2 pt-0 overflow-hidden">
              <div 
                className="grid gap-1.5 min-w-0"
                style={{
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                }}
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-xs text-muted-foreground truncate">HE Damage</span>
                  <span className="text-sm font-semibold truncate">{ammo.heDamage}</span>
                </div>
                <FieldDisplay fieldKey="heDamageRadius" value={ammo.heDamageRadius} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="suppress" value={ammo.suppress} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="suppressDamagesRadius" value={ammo.suppressDamagesRadius} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="damageFamily" value={ammo.damageFamily} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="damageIndex" value={ammo.damageIndex} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="damageDropOff" value={ammo.damageDropOff} fieldType="weapon-ammo" className="min-w-0" />
              </div>
            </CardContent>
          </Card>

          {/* Armor Penetration Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Armor Penetration</CardTitle>
            </CardHeader>
            <CardContent className="p-2 pt-0 overflow-hidden">
              <div 
                className="grid gap-1.5 min-w-0"
                style={{
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                }}
              >
                <FieldDisplay fieldKey="penetration" value={ammo.penetration} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="isKinetic" value={ammo.isKinetic} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="piercingWeapon" value={ammo.piercingWeapon} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="tandemCharge" value={ammo.tandemCharge} fieldType="weapon-ammo" className="min-w-0" />
              </div>
            </CardContent>
          </Card>

          {/* Range Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Range Statistics</CardTitle>
            </CardHeader>
            <CardContent className="p-2 pt-0 overflow-hidden">
              <div 
                className="grid gap-1.5 min-w-0"
                style={{
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                }}
              >
                <FieldDisplay fieldKey="groundMaxRange" value={ammo.groundMaxRange} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="groundMinRange" value={ammo.groundMinRange} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="heliMaxRange" value={ammo.heliMaxRange} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="heliMinRange" value={ammo.heliMinRange} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="planeMaxRange" value={ammo.planeMaxRange} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="planeMinRange" value={ammo.planeMinRange} fieldType="weapon-ammo" className="min-w-0" />
              </div>
            </CardContent>
          </Card>

          {/* Timing Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Timing & Rate of Fire</CardTitle>
            </CardHeader>
            <CardContent className="p-2 pt-0 overflow-hidden">
              <div 
                className="grid gap-1.5 min-w-0"
                style={{
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                }}
              >
                <FieldDisplay fieldKey="aimingTime" value={ammo.aimingTime} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="reloadTime" value={ammo.reloadTime} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="salvoLength" value={ammo.salvoLength} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="timeBetweenSalvos" value={ammo.timeBetweenSalvos} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="ammunitionPerSalvo" value={ammo.ammunitionPerSalvo} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="rateOfFire" value={ammo.rateOfFire} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="trueRateOfFire" value={ammo.trueRateOfFire} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="supplyCostPerSalvo" value={ammo.supplyCostPerSalvo} fieldType="weapon-ammo" className="min-w-0" />
              </div>
            </CardContent>
          </Card>

          {/* Accuracy Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Accuracy Statistics</CardTitle>
            </CardHeader>
            <CardContent className="p-2 pt-0 overflow-hidden">
              <div 
                className="grid gap-1.5 min-w-0"
                style={{
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                }}
              >
                <FieldDisplay fieldKey="staticAccuracy" value={ammo.staticAccuracy} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="movingAccuracy" value={ammo.movingAccuracy} fieldType="weapon-ammo" className="min-w-0" />
                {ammo.maxStaticAccuracy !== undefined && (
                  <FieldDisplay fieldKey="maxStaticAccuracy" value={ammo.maxStaticAccuracy} fieldType="weapon-ammo" className="min-w-0" />
                )}
                {ammo.maxMovingAccuracy !== undefined && (
                  <FieldDisplay fieldKey="maxMovingAccuracy" value={ammo.maxMovingAccuracy} fieldType="weapon-ammo" className="min-w-0" />
                )}
                {ammo.staticPrecisionBonusPerShot !== undefined && (
                  <FieldDisplay fieldKey="staticPrecisionBonusPerShot" value={ammo.staticPrecisionBonusPerShot} fieldType="weapon-ammo" className="min-w-0" />
                )}
                {ammo.movingPrecisionBonusPerShot !== undefined && (
                  <FieldDisplay fieldKey="movingPrecisionBonusPerShot" value={ammo.movingPrecisionBonusPerShot} fieldType="weapon-ammo" className="min-w-0" />
                )}
                {ammo.maxSuccessiveHitCount !== undefined && (
                  <FieldDisplay fieldKey="maxSuccessiveHitCount" value={ammo.maxSuccessiveHitCount} fieldType="weapon-ammo" className="min-w-0" />
                )}
                {ammo.dispersionAtMaxRange !== undefined && (
                  <FieldDisplay fieldKey="dispersionAtMaxRange" value={ammo.dispersionAtMaxRange} fieldType="weapon-ammo" className="min-w-0" />
                )}
                {ammo.dispersionAtMinRange !== undefined && (
                  <FieldDisplay fieldKey="dispersionAtMinRange" value={ammo.dispersionAtMinRange} fieldType="weapon-ammo" className="min-w-0" />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Other Properties Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Other Properties</CardTitle>
            </CardHeader>
            <CardContent className="p-2 pt-0 overflow-hidden">
              <div 
                className="grid gap-1.5 min-w-0"
                style={{
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                }}
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-xs text-muted-foreground truncate">Descriptor</span>
                  <span className="text-sm font-semibold font-mono text-xs truncate">{ammo.descriptorName}</span>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs text-muted-foreground truncate">Category</span>
                  <span className="text-sm font-semibold truncate">{renderOptionalValue(ammo.minMaxCategory)}</span>
                </div>
                <FieldDisplay fieldKey="firesLeftToRight" value={ammo.firesLeftToRight} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="distanceToTarget" value={ammo.distanceToTarget} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="numberOfSimultaneousProjectiles" value={ammo.numberOfSimultaneousProjectiles} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="noiseMalus" value={ammo.noiseMalus} fieldType="weapon-ammo" className="min-w-0" />
                <FieldDisplay fieldKey="shotsBeforeMaxNoise" value={ammo.shotsBeforeMaxNoise} fieldType="weapon-ammo" className="min-w-0" />
                {ammo.textureId && (
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs text-muted-foreground truncate">Texture ID</span>
                    <span className="text-sm font-semibold font-mono text-xs truncate">{ammo.textureId}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Units Using This Ammo */}
        {unitsUsingAmmo.length > 0 && (
          <Card className="mb-4">
            <CardHeader>
              <CardTitle>Units Using This Ammo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                {unitsUsingAmmo.map((unit) => (
                  <div
                    key={unit.descriptorName}
                    className="bg-card rounded-lg flex flex-col items-center cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden"
                    onClick={() => navigate(`/units/${encodeURIComponent(unit.descriptorName)}`)}
                  >
                    <div className="relative w-full aspect-[360/184] flex items-center justify-center overflow-hidden bg-muted/20">
                      <img
                        src={getUnitImagePath(unit.descriptorName)}
                        alt={unit.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </div>
                    <h5 className="text-xs font-medium text-center p-1 leading-tight">{unit.name}</h5>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

